import { Capacitor } from "@capacitor/core";
// Static import — dynamic import() chunks never load in the Capacitor
// WKWebView (see src/lib/nativePush.ts). Guarded by isIosNative(), not
// isNativePlatform() — @capacitor-community/apple-sign-in has no Android
// implementation. The web branch below is no way in for Android either (see
// appleSignInAvailable).
import { SignInWithApple } from "@capacitor-community/apple-sign-in";

// Shared by AppleButton.tsx and Step1Welcome.tsx's own inline flow (kept
// separate per the note below) so the platform check can't drift out of sync
// between the three call sites again.
export const isIosNative = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

// Sign in with Apple works on the iPhone (Apple's own sheet, the plugin
// above) and on the web (Apple's pop-up). Not inside the Android app: there
// is no plugin, and the Capacitor WebView opens no pop-up windows, so the web
// flow opened Chrome or never came back and left the buttons stuck busy. The
// Android app shows no Apple button; Google is offered everywhere
// (2026-10-10).
export const appleSignInAvailable = (): boolean => Capacitor.getPlatform() !== "android";

// Triggers a fresh Sign in with Apple and returns the new identityToken.
// Used to re-mint an expired Apple token at Step 6 (Connect Bank) so a talent
// who lingered past the token's lifetime can recover without dead-ending.
//
// NOTE: this mirrors the inline Apple flow in Step1Welcome.tsx#handleApple.
// Follow-up: dedupe by having Step1 call this helper too (kept separate now to
// keep this production hotfix off the known-good Step 1 sign-in path).

export interface AppleSignInResult {
  idToken: string;
  user?: { name?: string; email?: string };
}

// Resolves with a fresh token, or null if Apple returned no identityToken.
// Throws on real failures (including user cancellation) — callers decide how
// to treat cancellation.
export async function signInWithApple(): Promise<AppleSignInResult | null> {
  const clientId = process.env.REACT_APP_APPLE_CLIENT_ID || "com.requesta.signin";
  const redirectURI =
    process.env.REACT_APP_APPLE_REDIRECT_URI ||
    "https://www.requesta.app/auth/apple/callback";

  if (isIosNative()) {
    const result = await SignInWithApple.authorize({
      clientId,
      redirectURI,
      scopes: "email name",
    });
    const idToken = result?.response?.identityToken;
    if (!idToken) return null;
    return {
      idToken,
      user: {
        name: result.response.givenName
          ? `${result.response.givenName} ${result.response.familyName || ""}`.trim()
          : undefined,
        email: result.response.email || undefined,
      },
    };
  }

  // Web fallback — load Apple's JS SDK on demand, then open the popup.
  if (!window.AppleID) {
    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    document.head.appendChild(script);
    await new Promise((resolve) => (script.onload = resolve));
  }

  window.AppleID?.auth.init({ clientId, scope: "email name", redirectURI, usePopup: true });

  const res = await window.AppleID?.auth.signIn();
  const idToken = res?.authorization?.id_token;
  if (!idToken) return null;
  return {
    idToken,
    user: {
      name: res.user?.name
        ? `${res.user.name.firstName || ""} ${res.user.name.lastName || ""}`.trim()
        : undefined,
      email: res.user?.email || undefined,
    },
  };
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: any) => void;
        signIn: () => Promise<any>;
      };
    };
  }
}
